import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import BuzzLogo from "../components/BuzzLogo";
import { CheckCircle, Warning, Clock, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";

// Public Twilio Setup Playbook — authoritative reference for operators
// walking through the multi-day Twilio onboarding. Also linked from the
// in-app A2P wizard and the DashVoice panel.
export default function TwilioSetup() {
  const [pb, setPb] = useState(null);
  useEffect(() => { api.get("/twilio/setup/playbook").then(r => setPb(r.data)).catch(() => {}); }, []);

  // Pre-split the steps into phase-1 / phase-2 buckets so the two <section>
  // blocks below don't re-run filter() on every render. Only recomputes when
  // the playbook payload changes.
  const phase1Steps = useMemo(() => (pb?.steps || []).filter(s => s.phase === "phase_1"), [pb]);
  const phase2Steps = useMemo(() => (pb?.steps || []).filter(s => s.phase === "phase_2"), [pb]);

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="twilio-setup-page">
      {/* Top nav */}
      <div className="border-b border-[#E1DFD5] bg-white/60 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#1E3A2F] flex items-center justify-center"><BuzzLogo size={22} /></div>
            <span className="font-heading font-extrabold text-lg">BuzzerAI</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/faq" className="text-[#5C6B61] hover:text-[#121914]">FAQ</Link>
            <Link to="/troubleshooting" className="text-[#5C6B61] hover:text-[#121914]">Troubleshooting</Link>
            <Link to="/dashboard/a2p" className="btn-primary text-sm" data-testid="setup-open-wizard">Open A2P wizard</Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-6">
        <div className="overline mb-3">Twilio setup · The real playbook</div>
        <h1 className="text-4xl sm:text-5xl font-heading font-extrabold tracking-tight leading-tight">
          Voice AI in minutes. <span className="text-[#D96C5B]">Full SMS compliance in ~1 week.</span>
        </h1>
        <p className="text-[#5C6B61] mt-4 max-w-2xl leading-relaxed">
          Twilio's US SMS onboarding is genuinely slow — 3 to 7 business days for A2P Campaign approval, sometimes longer. This page is the honest walkthrough. Your Voice AI answers phones on day one. SMS quality improves once carrier approval lands.
        </p>
      </section>

      {/* Timeline */}
      <section className="max-w-4xl mx-auto px-6 pb-10">
        <div className="paper-card p-6">
          <div className="overline mb-4">Realistic timeline</div>
          <div className="space-y-3">
            {(pb?.timeline || []).map((t) => (
              <div key={t.label} className="grid grid-cols-[auto_1fr] gap-4 items-start" data-testid={`timeline-${t.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className={`w-11 h-11 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${
                  t.tone === "green" ? "bg-[#C2D1C1] text-[#1E3A2F]" :
                  t.tone === "amber" ? "bg-[#FEF3C7] text-[#92400E]" :
                  "bg-[#FEF2F0] text-[#A5382A]"
                }`}>
                  <Clock size={16} weight="fill" />
                </div>
                <div>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <div className="font-heading font-extrabold text-base text-[#121914]">{t.label}</div>
                    <div className="text-xs text-[#5C6B61]">· {t.duration}</div>
                  </div>
                  <p className="text-sm text-[#5C6B61] leading-snug mt-0.5">{t.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phase 1 — voice */}
      <section className="max-w-4xl mx-auto px-6 pb-6">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle size={22} color="#1E3A2F" weight="fill" />
          <h2 className="text-2xl font-heading font-extrabold">Phase 1 — Voice AI live today</h2>
        </div>
        <div className="space-y-3">
          {phase1Steps.map((s, i) => (
            <StepCard key={s.title} idx={i + 1} step={s} tone="green" />
          ))}
        </div>
      </section>

      {/* Phase 2 — SMS compliance */}
      <section className="max-w-4xl mx-auto px-6 pb-10">
        <div className="flex items-center gap-2 mb-4">
          <Warning size={22} color="#D96C5B" weight="fill" />
          <h2 className="text-2xl font-heading font-extrabold">Phase 2 — SMS compliance (this is the slow part)</h2>
        </div>
        <div className="paper-card p-4 mb-4 bg-[#FEF3C7] border border-[#F59E0B]/40 text-sm text-[#78350F] leading-snug">
          <b>The unlock:</b> use our in-app A2P wizard once, get a copy-paste-ready PDF, and never touch these forms by hand again. Opens the wizard →
          <Link to="/dashboard/a2p" className="ml-1 underline font-semibold">/dashboard/a2p</Link>
        </div>
        <div className="space-y-3">
          {phase2Steps.map((s, i) => (
            <StepCard key={s.title} idx={i + 1} step={s} tone="amber" />
          ))}
        </div>
      </section>

      {/* Common errors */}
      <section className="max-w-4xl mx-auto px-6 pb-10">
        <div className="overline mb-3">Twilio error decoder</div>
        <p className="text-sm text-[#5C6B61] mb-4 leading-relaxed">If Twilio shows a red banner or a call log with an error code, look it up here first. Codes are Twilio-side; fixes are one-liners.</p>
        <div className="paper-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#1E3A2F] text-white">
              <tr>
                <th className="text-left py-2 px-4 font-heading font-extrabold text-xs uppercase tracking-wide">Code</th>
                <th className="text-left py-2 px-4 font-heading font-extrabold text-xs uppercase tracking-wide">Meaning</th>
                <th className="text-left py-2 px-4 font-heading font-extrabold text-xs uppercase tracking-wide">Fix</th>
              </tr>
            </thead>
            <tbody>
              {(pb?.common_errors || []).map((e) => (
                <tr key={e.code} className="border-t border-[#E1DFD5]" data-testid={`error-row-${e.code}`}>
                  <td className="py-3 px-4 font-mono text-[#A5382A] font-bold align-top">{e.code}</td>
                  <td className="py-3 px-4 text-[#121914] font-semibold align-top">{e.meaning}</td>
                  <td className="py-3 px-4 text-[#5C6B61] leading-snug align-top">{e.fix}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Copy-paste language */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="overline mb-3">Recommended opt-in + response language</div>
        <p className="text-sm text-[#5C6B61] mb-4 leading-relaxed">Copy these into your Twilio Campaign form (Twilio is strict on wording). Replace <span className="font-mono text-[#1E3A2F]">{"{business_name}"}</span> and <span className="font-mono text-[#1E3A2F]">{"{business_phone}"}</span>.</p>
        {pb?.recommended_opt_in_language && (
          <div className="space-y-3">
            {Object.entries(pb.recommended_opt_in_language).map(([k, v]) => (
              <div key={k} className="paper-card p-4">
                <div className="overline mb-2">{k.replace(/_/g, " ")}</div>
                <p className="text-sm text-[#121914] leading-relaxed font-mono">{v}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Footer CTA */}
      <section className="bg-[#1E3A2F] text-[#F7F6F2] px-6 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#F5C543]/20 border border-[#F5C543]/40 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#F5C543] mb-4">
            <Sparkle size={14} weight="fill" /> Skip the pain
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-extrabold mb-3">Fill the form once. We generate the copy-paste kit.</h2>
          <p className="text-[#E1DFD5] mb-6 leading-relaxed max-w-xl mx-auto">The A2P wizard collects every field Twilio will ask for, then hands you a PDF you can paste from. Cuts a 2-hour first setup down to 15 minutes of typing.</p>
          <Link to="/dashboard/a2p" className="btn-primary" data-testid="setup-cta-wizard">Open the A2P wizard</Link>
        </div>
      </section>
    </div>
  );
}

const StepCard = ({ idx, step, tone }) => (
  <div className="paper-card p-5 flex gap-4 items-start" data-testid={`step-${idx}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-extrabold text-sm flex-shrink-0 ${
      tone === "green" ? "bg-[#C2D1C1] text-[#1E3A2F]" : "bg-[#FEF3C7] text-[#92400E]"
    }`}>{idx}</div>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-baseline gap-2 mb-1">
        <div className="font-heading font-extrabold text-base text-[#121914]">{step.title}</div>
        <div className="text-xs text-[#5C6B61]">· {step.eta}</div>
      </div>
      <p className="text-sm text-[#5C6B61] leading-relaxed">{step.detail}</p>
      {step.console_url && (
        <a href={step.console_url} target="_blank" rel="noreferrer" className="text-sm text-[#1E3A2F] underline inline-flex items-center gap-1 mt-2 font-semibold">
          Open in Twilio Console <ArrowSquareOut size={12} />
        </a>
      )}
    </div>
  </div>
);
